// src/utils/resolveSurface.js
//
// The single resolver that maps a user's funnel axes — { role, memberType,
// ageTier } plus the org-configured `memberSurface` — to the surface (home
// view) they should land on. See docs/eri-hub-member-funnel-spec.md §4.
//
// TAG vs SURFACE (the decoupling): a member-type id ('athlete' / 'spouse' /
// 'child') is now purely a TAG — a stable label for segmentation/reporting.
// The destination is decided by the `surface` field the org configures on
// that slot (memberTypes.js), passed in here as `memberSurface`. This is what
// lets the same 'spouse' tag route to the Family/Caregiver hub for NHL Alumni
// (a partner really is a caregiver) but to the plain adult surface for a
// corporate org like Alberta Blue Cross (a spouse is just another member).
//
// Org function (`role`, email-derived) wins first: staff always route to their
// console regardless of which persona card they tapped. The content gate
// (`ageTier`) wins next — configured surfaces only decide for adults. Orgs
// that haven't configured a surface fall back to the legacy id-based routing.
//
// Returned surface ids and their consumers:
//   'executive' -> ExecutivePortal      (admin / super_admin)
//   'coach'     -> CoachPortal           (coach)
//   'family'    -> SpouseHubView         (memberSurface 'family' / legacy spouse)
//   'parent'    -> ParentHubView         (memberSurface 'parent': youth-sport parent)
//   'kidz'      -> KidzCorner            (kid age tier)
//   'teen'      -> TeenHubView           (teen age tier)  <- surface to build
//   'athlete'   -> adult home (DashboardView etc.)        (default)

// How an org wants its under-18s to land (organizations/{id}.youthSurface):
//   'age'     - by age: Kidz Corner under 13, Teen Hub 13-17. The member is
//               someone's child (NHL Alumni). The default.
//   'athlete' - the athlete experience for every member under 18. The member IS
//               the player (Hockey Alberta). Content is the org allowlist under
//               the youth floor (utils/navAccess.js).
export const YOUTH_SURFACE_OPTIONS = [
  { id: 'age', label: 'By age: Kidz Corner under 13, Teen Hub 13–17 (family orgs)' },
  { id: 'athlete', label: 'Athlete experience for every member under 18 (youth sport)' }
];

export function resolveSurface({ role, memberType, ageTier, memberSurface, youthSurface } = {}) {
  if (role === 'admin' || role === 'super_admin') return 'executive';
  if (role === 'coach') return 'coach';

  // Minors: a youth-sport org puts them on the athlete surface; a family org
  // keeps the age hubs. Either way the age gates below the surface (consent,
  // crisis lines, the youth check-in, the content floor) still apply.
  if (ageTier === 'kid' || ageTier === 'teen') {
    if (youthSurface === 'athlete') return 'athlete';
    return ageTier === 'kid' ? 'kidz' : 'teen';
  }

  // Explicit per-org surface (from the org's memberTypes config) decides for adults.
  if (memberSurface === 'parent')  return 'parent';
  if (memberSurface === 'family')  return 'family';
  if (memberSurface === 'athlete') return 'athlete';

  // Legacy fallback — orgs with no configured surface keep the old id-based behaviour.
  if (memberType === 'spouse') return 'family';
  return 'athlete';
}
